import React from 'react';
import TiltedCard from './reactbits/TiltedCard';

const SectionHeading = ({ children }) => (
  <div className="text-center mb-10">
    <h2 className="text-subsection font-bold font-heading text-gray-900" style={{textWrap: 'balance'}}>
      {children}
    </h2>
    <div className="mt-3 mx-auto w-12 h-0.5 bg-primary-700 rounded-full" />
  </div>
);

const Services = ({ admissionServices = [] }) => {
  return (
    <section id="services" className="py-16 md:py-24">
      <div className="container mx-auto px-4">

        {/* Section intro */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-section font-bold text-gray-900 font-heading" style={{textWrap: 'balance'}}>
            Our Educational Services
          </h2>
          <p className="mt-4 text-gray-600 text-lg" style={{textWrap: 'pretty'}}>
            Comprehensive admission support for students aspiring to excel in various professional fields. We guide you from entrance exam preparation to final admission.
          </p>
        </div>

        {/* Service Cards */}
        {admissionServices && admissionServices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {admissionServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3 font-heading">{service.title}</h3>
                {service.description && (
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                )}

                {service.features && (
                  <ul className="space-y-1.5 mb-5">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <svg className="w-4 h-4 text-primary-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                {service.quotaTypes && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Available Quotas</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {service.quotaTypes.map((quota, idx) => (
                        <span key={idx} className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium border border-primary-100">
                          {quota}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {service.serviceLocations && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Locations</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {service.serviceLocations.map((location, idx) => (
                        <span key={idx} className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                          {location}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {service.featuredColleges && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Featured Colleges</h4>
                    <ul className="space-y-1">
                      {Array.isArray(service.featuredColleges) &&
                        service.featuredColleges.slice(0, 5).map((college, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                            {typeof college === 'object' ? college.name : college}
                            {typeof college === 'object' && college.location && (
                              <span className="text-gray-400">({college.location})</span>
                            )}
                          </li>
                        ))
                      }
                      {Array.isArray(service.featuredColleges) && service.featuredColleges.length > 5 && (
                        <li className="text-xs text-primary-600">
                          + {service.featuredColleges.length - 5} more colleges
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="mt-auto pt-4">
                  <a
                    href={
                      service.serviceType === "BAMS Admission" ? "#bams-colleges" :
                      service.serviceType === "MBBS Admission" ? "#mbbs-india" :
                      service.serviceType === "Engineering Admission" ? "#engineering" : "#colleges"
                    }
                    className="inline-block px-5 py-2 bg-primary-800 text-white rounded-full text-sm font-medium hover:bg-primary-900 transition-colors duration-200"
                  >
                    Get More Details
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MBBS in India Section */}
        <div id="mbbs-india" className="mb-20">
          <SectionHeading>MBBS in India — Fee Structure 2026</SectionHeading>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 mobile-hidden-img">
              <TiltedCard
                imageSrc="/photos/medical-campus.jpg"
                altText="MBBS Admission Guide — medical college campus"
                containerHeight="420px"
                imageHeight="420px"
                rotateAmplitude={6}
                scaleOnHover={1.03}
                showTooltip={false}
                overlayContent={
                  <div className="p-5 text-white">
                    <h3 className="text-lg font-semibold font-heading">MBBS Admission Guide</h3>
                    <p className="text-sm opacity-90">Latest fee structure for private medical colleges</p>
                  </div>
                }
              />
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8 h-full flex flex-col">
                <p className="text-gray-700 mb-7 bg-slate-50 rounded-lg px-4 py-3 text-sm leading-relaxed">
                  Looking for MBBS admission in India? We provide comprehensive guidance for securing seats in top private medical colleges across India. Our experts help you navigate the admission process, from counseling to final seat confirmation.
                </p>

                <h3 className="text-base font-semibold text-gray-800 mb-3 font-heading">Private Medical Colleges — Sample Fee Structure</h3>
                <div className="overflow-x-auto mb-6 rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">College Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">First Year Fee</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Package</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      <tr>
                        <td className="px-4 py-3 text-gray-700">Shri Ram Murti Smarak Institute of Medical Sciences</td>
                        <td className="px-4 py-3 text-gray-600">Bareilly</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹22,07,672</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹85,84,524</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-gray-700">Rohilkhand Medical College & Hospital</td>
                        <td className="px-4 py-3 text-gray-600">Bareilly</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹21,19,461</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹81,87,575</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-gray-700">Rajshree Medical Research Institute</td>
                        <td className="px-4 py-3 text-gray-600">Bareilly</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹20,33,247</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">₹77,99,612</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <a
                    href="#colleges"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-800 text-white rounded-full font-medium hover:bg-primary-900 transition-colors duration-200"
                  >
                    View Complete Fee Structure
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Study Abroad — Nepal */}
        <div id="study-abroad" className="mb-20">
          <SectionHeading>MBBS in Nepal — Kathmandu University</SectionHeading>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8 h-full flex flex-col">
                <p className="text-gray-700 mb-7 bg-slate-50 rounded-lg px-4 py-3 text-sm leading-relaxed">
                  Study MBBS in Nepal at Kathmandu University affiliated colleges. Nepal offers quality medical education at affordable costs, with a curriculum similar to Indian medical colleges and recognition by the National Medical Commission (NMC).
                </p>

                <h3 className="text-base font-semibold text-gray-800 mb-4 font-heading">Featured Medical Colleges in Nepal</h3>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {['Manipal Academy', 'Nobel Medical College', 'Kathmandu University'].map((college, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
                      <img src="/assets/NepalFlag.svg" alt="Nepal" className="h-6 mx-auto mb-2" />
                      <p className="text-xs font-medium text-gray-700">{college}</p>
                    </div>
                  ))}
                </div>

                <ul className="space-y-2 mb-7">
                  {[
                    'NMC (National Medical Commission) Approved',
                    'English Medium of Instruction',
                    'Affordable Fee Structure',
                    'Similar Curriculum to Indian Medical Colleges',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-800 text-white rounded-full font-medium hover:bg-primary-900 transition-colors duration-200"
                  >
                    Get Free Counselling
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 mobile-hidden-img">
              <TiltedCard
                imageSrc="/photos/kathmandu.jpg"
                altText="Kathmandu cityscape — Nepal MBBS programs"
                containerHeight="420px"
                imageHeight="420px"
                rotateAmplitude={6}
                scaleOnHover={1.03}
                showTooltip={false}
                overlayContent={
                  <div className="p-5 text-white">
                    <h3 className="text-lg font-semibold font-heading">MBBS in Nepal</h3>
                    <p className="text-sm opacity-90">Kathmandu University Affiliated Colleges</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        {/* Engineering Section */}
        <div id="engineering" className="mb-20">
          <SectionHeading>Engineering Admission 2026</SectionHeading>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 mobile-hidden-img">
              <TiltedCard
                imageSrc="/Engineering.jpg"
                altText="Engineering college campus in UP"
                containerHeight="420px"
                imageHeight="420px"
                rotateAmplitude={6}
                scaleOnHover={1.03}
                showTooltip={false}
                overlayContent={
                  <div className="p-5 text-white">
                    <h3 className="text-lg font-semibold font-heading">Engineering Admission</h3>
                    <p className="text-sm opacity-90">Secure seats in top engineering colleges</p>
                  </div>
                }
              />
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8 h-full flex flex-col">
                <p className="text-gray-700 mb-7 bg-slate-50 rounded-lg px-4 py-3 text-sm leading-relaxed">
                  Low percentile in JEE? Don't worry. We can help you secure admission in top AKTU colleges through management quota and direct admission. Our counselors have helped thousands of students get into their dream engineering colleges.
                </p>

                <h3 className="text-base font-semibold text-gray-800 mb-4 font-heading">Featured Engineering Colleges</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-7">
                  {[
                    { name: 'GL Bajaj Institute', loc: 'Greater Noida' },
                    { name: 'NIET', loc: 'Greater Noida' },
                    { name: 'ABES Engineering', loc: 'Ghaziabad' },
                    { name: 'KIET Group', loc: 'Ghaziabad' },
                    { name: 'Galgotias University', loc: 'Greater Noida' },
                    { name: 'JIIT', loc: 'Noida' },
                  ].map((c, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-sm font-medium text-gray-800">{c.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{c.loc}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-800 text-white rounded-full font-medium hover:bg-primary-900 transition-colors duration-200"
                  >
                    Book Your Seat Now
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fee packages CTA — sourced from current marketing materials */}
        <div className="rounded-2xl overflow-hidden border border-primary-100">
          <div className="bg-primary-800 px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white font-heading">Just Qualified? Your Dream Is Still Possible.</h3>
              <p className="text-primary-200 text-sm mt-1">We place students in every NEET bracket — 168 to 600+. No advance payment.</p>
            </div>
            <a
              href="#contact"
              className="shrink-0 px-6 py-2.5 bg-white text-primary-800 rounded-full font-semibold text-sm hover:bg-primary-50 transition-colors duration-200"
            >
              Get Free Counselling
            </a>
          </div>
          <div className="bg-white px-8 py-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Indicative Package Range</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { prog: 'MBBS India',   fee: '₹65 L*' },
                { prog: 'MBBS Abroad',  fee: '₹20 L*' },
                { prog: 'BAMS',         fee: '₹10.5 L*' },
                { prog: 'BDS / BHMS',   fee: '₹6–8 L*' },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">{item.prog}</p>
                  <p className="text-base font-bold text-primary-800 font-heading">{item.fee}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">* Indicative total package. Actual fee depends on college and quota. Contact us for exact figures.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
